import React, { Component } from 'react';
import { observer, inject } from 'mobx-react';
import { Redirect } from 'react-router-dom';

// Import Components
import NewBatch from '~/common/components/NewBatch';
import BackButton from '~/common/components/BackButton';
import SelectedNumberButton from '~/common/components/SelectedNumberButton';
import SelectNumberButton from '~/common/components/SelectNumberButton';
import Centering from '~/common/components/Centering';
import NumberSelection from '~/common/components/NumberSelection';
import LoadingSpinner from '~/common/components/LoadingSpinner';
import Stepper from '~/common/components/Stepper';
import HeaderGroup from '~/common/components/HeaderGroup';

import { scBeacon, createPersonalizedPurchaseEvent, gtm, viewContentEvent } from '~/common/utils/tracking.js';
import {
    FLOW_STEP_NUMBERS,
    PATH_TRIAL_SIGNUP
} from '~/common/utils/consts.js';

// Import Stylesheet
import styles from './toll-free.css';

@inject('lockNumberStore', 'tollFreeNumbersStore', 'modalStore', 'progressStore', 'trialSignupStore')
@observer
export default class TollFree extends Component {
    componentDidMount() {
        window.scroll({ top: 0, left: 0, behavior: 'smooth' });

        const { progressStore, tollFreeNumbersStore, trialSignupStore } = this.props;

        // if preselected flag, remove it
        if (tollFreeNumbersStore.preselectedTollFreeIntent) tollFreeNumbersStore.setPreselectedTollFreeIntent(false);

        // checks if this is the app entry point and sets corresponding flag
        if (!progressStore.firstView) progressStore.setFirstView(FLOW_STEP_NUMBERS);

        const { isTrialFlow } = trialSignupStore;
        gtm(viewContentEvent(isTrialFlow ? 'trial flow' : 'buy flow', 'numbers', 'toll free'));

        // if numbers are not fetched, get them
        if (!tollFreeNumbersStore.fetchedTollFreeNumbers) tollFreeNumbersStore.getTollFreeNumbers();
        // if at least 1 800 number is not feched, get it
        if (!tollFreeNumbersStore.fetched800Numbers) tollFreeNumbersStore.get800Numbers();
    }

    refreshTollFreeNumbers(ev) {
        ev.preventDefault();
        this.props.tollFreeNumbersStore.getTollFreeNumbers();
    }

    lockSelectedNumber(ev, number) {
        const { lockNumberStore, modalStore, trialSignupStore } = this.props;

        ev.preventDefault();
        // set selected number in store
        lockNumberStore.setSelectedNumber(number);
        // set selected number type to toll free
        lockNumberStore.setSelectedNumberType('Toll Free');

        if (trialSignupStore.isTrialFlow && lockNumberStore.isSelectedAreaCodePremium) {
            modalStore.setModalOptions({
                childComponentString: 'upgradeModal',
                isModalClosable: true,
                modalType: 'xlarge',
                isModalOpen: true
            });
        } else {
            lockNumberStore.lockNumberToSession();

            // track SCBeacon: selected Toll-Free number picked by application at random
            scBeacon(createPersonalizedPurchaseEvent('1'));
        }
    }

    render() {
        const { lockNumberStore, progressStore, tollFreeNumbersStore, trialSignupStore } = this.props;
        // flag denoting numbers have been fetched, list of numbers, number fetching error
        const { tollFreeNumbers, tollFreeNumbersError, true800NumbersError, true800Numbers } = tollFreeNumbersStore;
        // flag denoting if number has been successfully locked, selected number
        const { numberIsLocked, lockingNumber, selectedNumberE164 } = lockNumberStore;

        // if number is successfully locked
        if (numberIsLocked && lockingNumber) {
            // redirect to the trial page if in trial mode
            const redirectView = trialSignupStore.isTrialFlow ? PATH_TRIAL_SIGNUP : progressStore.nextStepFromNumbers;
            // redirect to billing page or plans page if plan is not selected
            return (
                <Redirect push={true} to={redirectView} />
            );
        }

        return (
            <main className={styles.wrapper}>
                <Centering lessBottomMargin relative={true}>
                    <Stepper currentStep={FLOW_STEP_NUMBERS} />
                    <BackButton currentStep={FLOW_STEP_NUMBERS} />
                    <HeaderGroup>
                        <h1>
                            Select your toll free number.
                        </h1>
                    </HeaderGroup>
                    {(true800NumbersError || tollFreeNumbersError) &&
                        <p className={styles['error-loading']}>
                            Oops! Something went wrong.
                            <span className={styles.link} onClick={ev => this.refreshTollFreeNumbers(ev)}>
                                Please try again
                            </span>
                        </p>
                    }
                    {tollFreeNumbersStore.fetchedTollFreeNumbers &&
                        <div>
                            <p className={styles.recommended}>
                                We recommend...
                            </p>
                            {trialSignupStore.isTrialFlow && tollFreeNumbersStore.trialValidNumber ?
                                <SelectedNumberButton
                                    number={tollFreeNumbersStore.trialValidNumber}
                                    onClick={ev => this.lockSelectedNumber(ev, tollFreeNumbersStore.trialValidNumber)}
                                />
                                :
                                <SelectedNumberButton
                                    number={true800Numbers[0]}
                                    onClick={ev => this.lockSelectedNumber(ev, true800Numbers[0])}
                                />
                            }
                        </div>
                    }
                </Centering>
                {tollFreeNumbersStore.fetchedTollFreeNumbers ?
                    <NumberSelection>
                        <p className={styles.copy}>
                            Or select from these great numbers:
                        </p>
                        <div className={styles['numbers-wrapper']}>
                            {tollFreeNumbers.map((number) => {
                                return (
                                    <SelectNumberButton
                                        key={number.E164}
                                        number={number}
                                        onClick={ev => this.lockSelectedNumber(ev, number)}
                                        selected={number.E164 === selectedNumberE164}
                                    />
                                );
                            })}
                        </div>
                        <div className={styles['new-batch']}>
                            <NewBatch onClick={ev => this.refreshTollFreeNumbers(ev)} />
                            <p className={styles['new-batch-text']}>800, 833, 844, 855, 866, 877, and 888 numbers are ALL toll free!</p>
                        </div>
                    </NumberSelection>
                    :
                    <div className={styles['loading-container']}>
                        <LoadingSpinner />
                    </div>
                }
            </main>
        );
    }
}
